import { get, isBoolean, unionWith, uniq } from "lodash";

import { COLORS } from "@treelab/panda";

import Chart, { IChartProps } from "./Chart";

import { IChartFormValue } from "../types/form-type";
import { ITableData, IWrokspaceData } from "../types/table-type";
import { ChartType } from "../types/global-type";
import { IDatasetSource, IDatasetTransform } from "../types/echarts-type";

import { LEGEND_HEIGHT, LEGEND_PAGE_ICON_PATH, LEGEND_PAGE_ICON_SIZE } from "../constants/chart";

import {
  getXAxisData,
  getGridClientRect,
  getXAxisLabelOptionByCategory,
  getOptionColorByValue,
  getRealColumn,
  showUseOptionColorSwitch,
} from "../utils/option_main";
import { checkGroupId } from "../utils/option_secondary";
import { basicSortFunc } from "../utils/sort";
import { getColors } from "../utils/colors";

/**
 * 通用的柱状图 abstract class 因为 柱状图 和 堆叠柱状图 具有相同的部分逻辑 这部分逻辑由当前 class 实现
 */
abstract class BasicBarChart extends Chart {
  public dimensionName: string;
  public isXAxisLabelRotate?: boolean;
  public groupId: string;

  constructor(props: IChartProps) {
    super(props);
    this.dimensionName = "";
    this.isXAxisLabelRotate = undefined;
    this.groupId = checkGroupId(this.formValue, this.tableData, this.formValue.chartType);
  }

  public calculationOther(dataset: (IDatasetSource | IDatasetTransform)[]) {
    this.dimensionName = get(dataset, "[0].dimensions[0]");
  }

  public getLegend(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isShowLegend } = formValue;
    let data: object[] | null = null;
    // 通过 data 强制设置颜色 只有开启了 分组 才需要
    const groupColumn = getRealColumn(this.groupId, tableData);
    if (isShowLegend && groupColumn && this.checkGroupColumnHasColor()) {
      const sourceData = (get(this.datasetObj, "[0].source") as string[][]) || [];
      const colorByOptionId = getOptionColorByValue({
        columns: tableData.columns,
        columnId: this.groupId,
      });
      data = unionWith(sourceData, (item1, item2) => item1[2] === item2[2]).map((item, index) => ({
        name: item[2],
        icon: "roundRect",
        itemStyle: formValue.useOptionColor ? {
          color: colorByOptionId[item[2]] || getColors(index, item[2]),
        } : null,
      }));

      const sortFunc = basicSortFunc({
        columType: groupColumn.type,
        columnOptions: groupColumn.typeOptions,
        valueKey: "name",
      });

      data.sort((item1, item2) => {
        return sortFunc(item1 as { [key in string]: string | number }, item2 as { [key in string]: string | number });
      });
    }

    const legend = isShowLegend
      ? {
        type: "scroll",
        show: true,
        icon: "roundRect",
        width: this.getLegendContainerWidth(),
        height: LEGEND_HEIGHT,
        top: this.isExpand ? 20 : 0,
        left: this.isExpand ? 32 : 24,
        itemWidth: 8,
        itemHeight: 6,
        itemGap: 16,
        textStyle: {
          color: `${COLORS.grey}`,
          fontSize: 12,
          lineHeight: 16,
          overflow: "truncate",
        },
        formatter: (name: any) => {
          return this.basicFormatLegend(name);
        },
        pageIconColor: "rgba(131, 134, 143, 1)",
        pageIconInactiveColor: "rgba(162, 165, 177, 0.6)",
        pageTextStyle: {
          color: "rgba(131, 134, 143, 1)",
          fontWeight: 400,
        },
        pageIcons: LEGEND_PAGE_ICON_PATH,
        pageIconSize: LEGEND_PAGE_ICON_SIZE,
        data,
      }
      : { show: false };
    return legend;
  }

  public getXAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    let data = getXAxisData(get(this.datasetObj, "[0].source"));
    const isGlobalFormat = this.calculation.checkGlobalFormat();
    const xAxis = {
      data: data,
      type: "category",
      show: true,
      axisLabel: getXAxisLabelOptionByCategory(
        data,
        this.getCanvasWidth(),
        this.getCanvasHeight(),
        this.isExpand,
        !!this.formValue.isShowYAxis,
        isGlobalFormat ? this.formatDimension : undefined
      ),
      axisTick: {
        alignWithLabel: true,
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: COLORS.greyLight2,
        },
      },
    };
    this.isXAxisLabelRotate = !!get(xAxis.axisLabel, "rotate");
    return xAxis;
  }

  public getYAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isShowYAxis = false } = formValue;
    return isShowYAxis
      ? {
        type: "value",
        show: true,
        splitLine: {
          show: true,
          lineStyle: {
            type: [2, 2],
          },
        },
        name: this.getYAxisName(),
        nameTextStyle: {
          color: COLORS.greyLight1,
        },
        nameGap: 8,
        axisLabel: {
          color: COLORS.grey,
          fontSize: 12,
          formatter: this.formatYAxisLabel,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: COLORS.greyLight2,
          },
        },
      }
      : { show: false };
  }

  public getGrid() {
    const { isShowYAxis, isShowLegend, chartType } = this.formValue;
    const data = getXAxisData(get(this.datasetObj, "[0].source"));
    const isGlobalFormat = this.calculation.checkGlobalFormat();

    let isRotate = this.isXAxisLabelRotate;
    if (!isBoolean(isRotate)) {
      const xAxisLabel = getXAxisLabelOptionByCategory(
        data,
        this.getCanvasWidth(),
        this.getCanvasHeight(),
        this.isExpand,
        !!isShowYAxis,
        isGlobalFormat ? this.formatDimension : undefined
      );
      const { rotate } = xAxisLabel as { rotate: number };
      isRotate = !!rotate;
    }
    const grid = {
      ...getGridClientRect(
        this.isExpand,
        ChartType.BASIC_BAR,
        isRotate,
        !!isShowYAxis,
        (isShowLegend && this.calculation.isGroup) || (isShowLegend && chartType === ChartType.STACKED_COLUMN_BAR)
      ),
    };
    return grid;
  }

  public checkChartOptionError() {
    return null;
  }

  /** 对于 bar 没有开启分组 使用 pieces 开启分组后 使用  categories 和 color */
  public getVisualMap(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    if (!formValue.useOptionColor) {
      return null;
    }
    if (!showUseOptionColorSwitch({ formValue, tableData })) {
      return null;
    }

    const colorByOptionId = getOptionColorByValue({
      columns: tableData.columns,
      columnId: this.groupId || formValue.dimensionId,
    });

    const sourceIndex = this.groupId ? 2 : 0;

    const source = (get(this.datasetObj, "[0].source") as string[][]) || [];

    const pieces = source.map((item, index) => ({
      gte: index,
      lte: index,
      color: colorByOptionId[item[sourceIndex]] || getColors(index, item[sourceIndex]),
    }));

    let categories = new Array<string>(source.length).fill("");
    let color = new Array<string>(source.length).fill("");

    source.forEach((item, index) => {
      categories[index] = item[sourceIndex];
      color[index] = colorByOptionId[item[sourceIndex]] || getColors(index, item[sourceIndex]);
    });

    categories = uniq(categories);

    const visualMap = {
      dimension: sourceIndex,
      type: "piecewise",
      pieces: this.groupId ? null : pieces,
      categories: this.groupId ? categories : null,
      target: this.groupId
        ? {
          inRange: {
            color: color,
          },
        }
        : null,
      show: false,
    };

    return visualMap;
  }
}

export default BasicBarChart;
